/**
 * Turns an arbitrary failure into one of the states spec §11 mandates, so no
 * screen has to string-match error messages itself.
 *
 * `apiClient.request()` collapses every failure into `new Error(body.error ||
 * 'HTTP <status>')`, which is why the status code is only recoverable from the
 * message text.
 */

/** @typedef {'loading'|'partial'|'empty'|'error'|'offline'|'permission'|'session'|'noSource'|'staleSource'|'quotaExceeded'} ViewStatus */

export const VIEW_STATUSES = [
  'loading',
  'partial',
  'empty',
  'error',
  'offline',
  'permission',
  'session',
  'noSource',
  'staleSource',
  'quotaExceeded'
];

const SESSION_PATTERNS = [
  /HTTP 401/i,
  /unauthori[sz]ed/i,
  /session (expired|not found)/i,
  /no active session/i,
  /^no session$/i,
  /token (expired|invalid)/i
];

const PERMISSION_PATTERNS = [
  /HTTP 40[37]/i,
  /forbidden/i,
  /access denied/i,
  /app_not_licensed/i,
  /tenant_suspended/i
];

const OFFLINE_PATTERNS = [
  /failed to fetch/i,
  /network ?error/i,
  /request timed out/i,
  /load failed/i,
  /ERR_(INTERNET_DISCONNECTED|NETWORK_CHANGED)/i
];

const NO_SOURCE_PATTERNS = [
  /no (knowledge )?source/i,
  /not indexed/i,
  /ckb (unavailable|not connected)/i,
  /no workspace/i
];

// The LLM guard's used-up-budget refusal: REST `429 {error:'quota_exceeded'}`
// (apiClient throws `new Error('quota_exceeded')`, `.status = 429`) and the
// WebSocket `chat:error` message text ("...token budget...used up").
const QUOTA_PATTERNS = [
  /quota_exceeded/i,
  /token budget.*used up/i
];

/**
 * @param {unknown} error
 * @returns {ViewStatus} one of 'error' | 'offline' | 'permission' | 'session' | 'noSource' | 'quotaExceeded'
 */
export function classifyError(error) {
  if (!error) return 'error';
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';

  const message = typeof error === 'string' ? error : error.message || '';
  const status = typeof error === 'object' ? error.status ?? error.statusCode : undefined;

  // A bare `status === 429` is NOT enough on its own: `generalLimiter`
  // (src/server/app.js) answers every `/api` route with HTTP 429 once a
  // client trips the 100 req/min ceiling, and apiClient.request() copies
  // that status onto every failed call regardless of cause. Only the
  // message — which apiClient sets from the LLM guard's `body.error` —
  // actually identifies the token-budget refusal.
  if (QUOTA_PATTERNS.some((pattern) => pattern.test(message))) return 'quotaExceeded';
  if (status === 401) return 'session';
  if (status === 402 || status === 403 || status === 423) return 'permission';

  if (SESSION_PATTERNS.some((pattern) => pattern.test(message))) return 'session';
  if (PERMISSION_PATTERNS.some((pattern) => pattern.test(message))) return 'permission';
  if (OFFLINE_PATTERNS.some((pattern) => pattern.test(message))) return 'offline';
  if (NO_SOURCE_PATTERNS.some((pattern) => pattern.test(message))) return 'noSource';
  return 'error';
}

/**
 * Technical detail worth showing *under* a plain-language headline. Spec §10.1
 * forbids a raw technical payload as the primary message, so callers render
 * this as secondary copy only.
 *
 * @param {unknown} error
 * @returns {string|null}
 */
export function errorDetail(error) {
  if (!error) return null;
  const message = typeof error === 'string' ? error : error.message || '';
  if (!message || /^HTTP \d+$/i.test(message)) return null;
  return message;
}

/**
 * The request id, if the failure carries one. Surfacing it is what makes a
 * support request actionable — without it a user can only say "it broke".
 *
 * @param {unknown} error
 * @returns {string|null}
 */
export function requestIdOf(error) {
  if (!error || typeof error === 'string') {
    return typeof error === 'string' ? matchRequestId(error) : null;
  }
  const direct = error.requestId || error.request_id || error.requestID || error.correlationId;
  if (direct) return String(direct);
  if (typeof error.headers?.get === 'function') {
    const header = error.headers.get('x-request-id');
    if (header) return header;
  }
  return matchRequestId(error.message || '');
}

function matchRequestId(text) {
  const match = /request[ -_]?id[:=\s]+([\w-]{6,})/i.exec(text || '');
  return match ? match[1] : null;
}
